import { Button, BUTTON_HEIGHT, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { noop } from 'lodash';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { TextField } from './TextField';

type Props = {
  value: number | undefined;
  onChange: (number: number) => void;
  style?: StyleProp<ViewStyle>;
  minValue?: number;
  small?: boolean;
  withBorder?: boolean;
};

export const NumberSpinner: React.FC<Props> = React.memo(
  ({ value, onChange, style, minValue = 1, small, withBorder = true }) => {
    const [number, setNumber] = React.useState<number | undefined>(value);
    const [fieldPrefixKeyRef, setFieldPrefixKeyRef] = React.useState<number>(0);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
    const spinTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
    const spinIntervalRef = React.useRef<ReturnType<typeof setInterval>>(null);
    const fieldValueRef = React.useRef<number | undefined>(undefined);
    const valueRef = React.useRef<number | undefined>(number);

    const stopSpinning = React.useCallback(() => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = null;
      }
      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }
    }, []);

    const handleChange = React.useCallback(
      (diff: number) => {
        setNumber((cur) => {
          const newValue = (cur || 0) + diff;

          if (newValue < minValue) {
            stopSpinning();
            return cur;
          }

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          timeoutRef.current = setTimeout(() => onChange(newValue), 150);
          valueRef.current = newValue;
          return newValue;
        });
      },
      [minValue, onChange, stopSpinning],
    );

    const startSpinning = React.useCallback(
      (spinAmount: number) => {
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
          spinTimeoutRef.current = null;
        }
        if (spinIntervalRef.current) {
          clearInterval(spinIntervalRef.current);
          spinIntervalRef.current = null;
        }
        handleChange(spinAmount);
        spinTimeoutRef.current = setTimeout(() => {
          spinIntervalRef.current = setInterval(
            () => handleChange(spinAmount),
            100,
          );
        }, 500);
      },
      [handleChange],
    );

    const handleSpinDown = React.useCallback(() => {
      startSpinning(-1);
    }, [startSpinning]);

    const handleSpinUp = React.useCallback(() => {
      startSpinning(1);
    }, [startSpinning]);

    const handleFieldChange = React.useCallback((newValue: string) => {
      // string.replaceAll is unavailable on Android for some reason
      const newValueParsed = parseInt(
        newValue
          .split('')
          .filter((a) => a !== '.')
          .join(''),
        10,
      );
      fieldValueRef.current = !Number.isNaN(newValueParsed)
        ? newValueParsed
        : -1;
    }, []);

    const handleFieldBlur = React.useCallback(() => {
      if (
        fieldValueRef.current !== undefined &&
        fieldValueRef.current >= minValue
      ) {
        valueRef.current = fieldValueRef.current;
        setNumber(fieldValueRef.current);
        onChange(fieldValueRef.current);
      } else {
        setFieldPrefixKeyRef(Math.round(Math.random() * 1000));
      }
    }, [minValue, onChange]);

    const handleFieldFocus = React.useCallback(() => {
      fieldValueRef.current = undefined;
    }, []);

    React.useEffect(() => {
      return () => {
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
          spinTimeoutRef.current = null;
        }
        if (spinIntervalRef.current) {
          clearInterval(spinIntervalRef.current);
          spinIntervalRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }, []);

    return (
      <View
        style={[
          {
            height: BUTTON_HEIGHT,
            borderWidth: withBorder ? 1 : 0,
            borderColor: theme.colors.blue80,
            borderRadius: 4,
            alignItems: 'center',
            flexDirection: 'row',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
          },
          small ? { width: undefined, flex: 1 } : { width: 120 },
          style,
        ]}
      >
        <Button
          icon={ICONS.MinusLight}
          iconSize={15}
          buttonTheme={ButtonTheme.themeless}
          onPress={noop}
          onPressIn={number === minValue ? undefined : handleSpinDown}
          onPressOut={stopSpinning}
          width={BUTTON_HEIGHT}
          glass={false}
        />
        {small ? (
          <View
            style={{
              flex: 1,
              height: BUTTON_HEIGHT,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Txt
              style={{
                textAlign: 'center',
                position: 'absolute',
                left: 0,
                right: 0,
                pointerEvents: 'none',
              }}
              size="small"
            >
              {number || number === 0 ? Math.max(number, 0) : 'NV'}
            </Txt>
          </View>
        ) : (
          <TextField
            value={number || number === 0 ? `${number}` : ''}
            key={`${fieldPrefixKeyRef}-${number}`}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            onFocus={handleFieldFocus}
            clearTextOnFocus
            onChangeDebounceMs={0}
            inputMode="numeric"
            containerStyle={{
              height: '100%',
              flex: 1,
              width: '100%',
              padding: 0,
              margin: 0,
            }}
            inputStyle={{
              textAlign: 'center',
              height: '100%',
              minHeight: undefined,
              flex: 1,
              paddingHorizontal: 0,
              margin: 0,
              padding: 0,
              width: '100%',
              borderLeftWidth: 1,
              borderLeftColor: theme.colors.blue80,
              borderRightWidth: 1,
              borderRightColor: theme.colors.blue80,
            }}
          />
        )}
        <Button
          icon={ICONS.PlusLight}
          iconSize={15}
          onPress={noop}
          onPressIn={handleSpinUp}
          onPressOut={stopSpinning}
          width={BUTTON_HEIGHT}
          glass={false}
        />
      </View>
    );
  },
);
